/**
 * Work Items API wrapper — thin per-endpoint functions for
 * `/api/admin/work-items/*` (WQ-298 / ADR-019 D2). Mirrors securityApi.ts:
 * takes the `authFetch` from AuthContext so auth-header handling stays central.
 */

import type {
  UpdateWorkItemBody,
  WorkItem,
  WorkItemFilters,
  WorkItemListResponse,
} from '../types/workItems';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/work-items';

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    const err = new Error(
      `HTTP ${res.status}${detail && typeof detail === 'object' && 'detail' in detail
        ? `: ${(detail as { detail: string }).detail}`
        : ''}`,
    );
    (err as Error & { status?: number; detail?: unknown }).status = res.status;
    (err as Error & { status?: number; detail?: unknown }).detail = detail;
    throw err;
  }
  return res.json() as Promise<T>;
}

export const workItemsApi = {
  list: async (
    authFetch: AuthFetch,
    filters: WorkItemFilters = {},
  ): Promise<WorkItemListResponse> => {
    const qs = buildQuery({
      lane: filters.lane,
      status: filters.status,
      severity_token: filters.severity_token,
      originating_skill: filters.originating_skill,
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    });
    const res = await authFetch(`${BASE}${qs}`);
    return jsonOrThrow<WorkItemListResponse>(res);
  },

  get: async (authFetch: AuthFetch, workItemId: string): Promise<WorkItem> => {
    const res = await authFetch(`${BASE}/${workItemId}`);
    return jsonOrThrow<WorkItem>(res);
  },

  update: async (
    authFetch: AuthFetch,
    workItemId: string,
    body: UpdateWorkItemBody,
  ): Promise<WorkItem> => {
    const res = await authFetch(`${BASE}/${workItemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return jsonOrThrow<WorkItem>(res);
  },
};
